import { useEffect, useState, useMemo, useRef } from "react";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { ArrowLeft, Radio, X, AlertCircle, CheckCircle2, AlertTriangle, PauseCircle } from "lucide-react";
import { EntryWeekSwitcher, useUrlWeek } from "@/pages/dashboard/kpi/EntryWeekSwitcher";

const ANSWER_STYLES = {
  YES: "bg-[#0e8a68] text-white border-[#0e8a68]",
  NO: "bg-[#ef5757] text-white border-[#ef5757]",
  // 2026-08-10 (Juliana): active NA was near-white so CSRs couldn't tell
  // it was selected. Now amber-filled to match the visual weight of YES/NO.
  NA: "bg-[#f59e0b] text-white border-[#f59e0b]",
  COMMERCIAL: "bg-[#0072bc] text-white border-[#0072bc]",
};
const ANSWER_INACTIVE = {
  YES: "bg-white text-[#0e8a68] border-[#c1eddc] hover:bg-[#e6f9f2]",
  NO: "bg-white text-[#ef5757] border-[#f4c9c9] hover:bg-[#fdecec]",
  NA: "bg-white text-[#b45309] border-[#fde68a] hover:bg-[#fef3c7]",
  COMMERCIAL: "bg-white text-[#0072bc] border-[#bae0f5] hover:bg-[#e7f4fb]",
};
const ANSWER_LABEL = {
  YES: "YES",
  NO: "NO",
  NA: "N/A",
  COMMERCIAL: "Commercial",
};

function fmtDayTime(iso) {
  // Fail-honest fallback (2026-07-31 bug fix): treat null/undefined/empty/epoch
  // as "no date on file" instead of rendering the classic 1970-01-01-in-CST
  // "Wed, Dec 31, 6:00 PM" placeholder. A wrong date is worse than no date.
  if (iso === null || iso === undefined || iso === "" || iso === 0 || iso === "0") {
    return "No date on file";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getTime() <= 0) {
    return "No date on file";
  }
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

// CC-215: completed jobs show when they were closed (finished_at); open jobs keep scheduled_at.
const jobDisplayAt = (j) => (j.status === "completed" && j.finished_at ? j.finished_at : j.scheduled_at);

function JobDetailModal({ jobId, onClose, onUpdated }) {
  const [job, setJob] = useState(null);
  const [saving, setSaving] = useState(null);
  // Required-field validation state. `missingKeys` = MANUAL items still blank
  // when the tech last hit "Mark Complete". Empty until the first attempt.
  const [missingKeys, setMissingKeys] = useState([]);
  const itemRefs = useRef({});

  useEffect(() => {
    if (jobId) api.get(`/kpi/jobs/${jobId}`).then(({ data }) => setJob(data));
  }, [jobId]);

  const setAnswer = async (key, answer) => {
    setSaving(key);
    try {
      const { data } = await api.patch(`/kpi/jobs/${jobId}/items/${key}`, { answer });
      setJob(data);
      if (onUpdated) onUpdated();
      // Clear this item from the missing-list if we just answered it —
      // instant visual feedback that they resolved the flag.
      setMissingKeys((prev) => prev.filter((k) => k !== key));
    } finally {
      setSaving(null);
    }
  };

  // MANUAL items that still have no answer (answer is null/undefined).
  // "NA" is a valid, deliberate answer and NEVER counts as missing.
  const missing = useMemo(() => {
    if (!job) return [];
    return job.items.filter(
      (it) => it.source === "MANUAL" && (it.answer === null || it.answer === undefined)
    );
  }, [job]);

  const attemptComplete = () => {
    if (missing.length === 0) {
      onClose();
      return;
    }
    setMissingKeys(missing.map((m) => m.key));
    // Scroll the first missing item into view so the tech doesn't have to hunt.
    const first = itemRefs.current[missing[0].key];
    if (first?.scrollIntoView) {
      first.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  if (!job) {
    return (
      <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="job-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[680px] my-4 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#eef1f6]">
          <button onClick={onClose} className="flex items-center gap-1.5 text-[12.5px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]" data-testid="job-back-btn">
            <ArrowLeft size={14} strokeWidth={1.8} /> Back to Jobs
          </button>
          <div className="mt-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display font-bold text-[19px] text-[#0e1420]">{job.customer}</div>
              <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
                <span className="font-semibold text-[#0e1420]">{job.tech_name}</span> · {job.job_number} · {fmtDayTime(jobDisplayAt(job))}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Revenue</div>
              <div className="font-mono-data font-bold text-[18px] text-[#0e1420]">${job.revenue.toLocaleString()}</div>
              {/* Compliance badge — consumes the server's canonical
                  `compliance_state` field (added 2026-07-24 per Brett):
                    INCOMPLETE → any required item unanswered (never says COMPLIANT
                                  even at high %; matches the checklist reality)
                    COMPLIANT  → 100% of required items = YES and 0 unanswered
                    PARTIAL    → some required = YES, none unanswered, but < 100%
                  Prior version rendered `${compliance_pct}% compliant` off a
                  proxy that treated unanswered items as excluded from the
                  denominator, producing "100% COMPLIANT" while 5/5 required
                  items were null. Directly contradictory to the checklist
                  below on the same page. */}
              {(() => {
                const state = job.compliance_state;
                const unanswered = job.required_unanswered ?? 0;
                if (state === "INCOMPLETE" || unanswered > 0) {
                  return (
                    <div
                      className="mt-1 text-[10.5px] font-mono-data uppercase tracking-widest rounded px-2 py-0.5 inline-block border bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]"
                      title={`${unanswered} required KPI item${unanswered === 1 ? "" : "s"} still unanswered.`}
                      data-testid="job-compliance-badge"
                    >
                      INCOMPLETE · {unanswered} unanswered
                    </div>
                  );
                }
                if (state === "COMPLIANT") {
                  return (
                    <div
                      className="mt-1 text-[10.5px] font-mono-data uppercase tracking-widest rounded px-2 py-0.5 inline-block border bg-[#e6f9f2] text-[#0e5a48] border-[#c1eddc]"
                      data-testid="job-compliance-badge"
                    >
                      COMPLIANT · 100%
                    </div>
                  );
                }
                // PARTIAL — some required answered YES, none unanswered, but <100%.
                const pct = job.compliance_pct ?? 0;
                const tone = pct >= 75
                  ? "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]"
                  : pct >= 50
                    ? "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]"
                    : "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]";
                return (
                  <div
                    className={`mt-1 text-[10.5px] font-mono-data uppercase tracking-widest rounded px-2 py-0.5 inline-block border ${tone}`}
                    data-testid="job-compliance-badge"
                  >
                    {pct}% compliant
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <div className="text-[11px] uppercase tracking-[.14em] text-[color:var(--cc-ink-muted)] font-semibold mb-3">
            KPI checklist · edit any item
          </div>
          {missingKeys.length > 0 && (
            <div
              data-testid="kpi-missing-banner"
              className="mb-3 flex items-start gap-2.5 rounded-lg border border-[#f4c9c9] bg-[#fdecec] px-3.5 py-2.5"
            >
              <AlertCircle size={16} strokeWidth={1.8} className="text-[#c73535] shrink-0 mt-0.5" />
              <div className="text-[12.5px] text-[#a02b2b] leading-relaxed">
                <span className="font-semibold">
                  {missingKeys.length === 1
                    ? "1 required item is unanswered:"
                    : `${missingKeys.length} required items are unanswered:`}
                </span>{" "}
                {missing.map((m) => m.label).join(", ")}. Pick <b>YES</b>, <b>N/A</b>, or <b>NO</b> to continue.
              </div>
            </div>
          )}
          <div className="space-y-2.5">
            {job.items.map((it) => {
              const isMissing = missingKeys.includes(it.key);
              const isUnanswered = it.answer === null || it.answer === undefined;
              return (
                <div
                  key={it.key}
                  ref={(el) => { itemRefs.current[it.key] = el; }}
                  className={`border rounded-lg p-3 transition-colors ${
                    isMissing
                      ? "border-[#ef5757] bg-[#fef7f7] ring-1 ring-[#ef5757]/40"
                      : "border-[#eef1f6]"
                  }`}
                  data-testid={`kpi-item-${it.key}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                      <div className="text-[13.5px] font-semibold text-[#0e1420]">{it.label}</div>
                      {it.source === "MANUAL" && (
                        <span
                          className="text-[9px] font-mono-data uppercase tracking-widest text-[#8a5b00]"
                          title="Required for job submission"
                        >
                          *required
                        </span>
                      )}
                    </div>
                    <span className={`text-[9.5px] font-mono-data uppercase tracking-widest rounded px-2 py-0.5 border ${
                      it.source === "AUTO"
                        ? "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]"
                        : "bg-[#f5f0ff] text-[#6e4fbb] border-[#dccdf3]"
                    }`}>
                      {it.source === "AUTO" ? "Auto · ST/Rilla" : "Manual"}
                    </span>
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(it.allowed_answers && it.allowed_answers.length > 0 ? it.allowed_answers : ["YES", "NA", "NO"]).map((a) => {
                      const active = it.answer === a;
                      const cls = active ? ANSWER_STYLES[a] : ANSWER_INACTIVE[a];
                      return (
                        <button
                          key={a}
                          disabled={saving === it.key}
                          onClick={() => setAnswer(it.key, a)}
                          data-testid={`kpi-answer-${it.key}-${a}`}
                          className={`flex-1 min-w-[60px] py-1.5 px-2 text-[12px] font-semibold rounded-md border transition-colors disabled:opacity-50 ${cls}`}
                        >
                          {ANSWER_LABEL[a] || a}
                        </button>
                      );
                    })}
                  </div>
                  {isUnanswered && (
                    <div
                      className="text-[10.5px] text-[color:var(--cc-ink-muted)] font-mono-data mt-1.5"
                      data-testid={`kpi-item-status-${it.key}`}
                    >
                      unanswered
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer — Mark Complete gate. Individual answers auto-save on click,
            so "Back to Jobs" leaves partial progress intact; "Mark Complete"
            requires all MANUAL items answered before it dismisses the modal. */}
        <div className="p-4 border-t border-[#eef1f6] bg-[#fafbfc] flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]" data-testid="kpi-manual-progress">
            {(() => {
              const manualCount = job.items.filter((i) => i.source === "MANUAL").length;
              const answered = manualCount - missing.length;
              return `${answered}/${manualCount} required items answered`;
            })()}
          </div>
          <button
            type="button"
            onClick={attemptComplete}
            data-testid="kpi-mark-complete-btn"
            className={`inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-md transition-colors ${
              missing.length === 0
                ? "bg-[#0e8a68] text-white hover:bg-[#0b7358] border border-[#0e8a68]"
                : "bg-[#0072bc] text-white hover:bg-[#005c99] border border-[#0072bc]"
            }`}
          >
            {missing.length === 0 ? (
              <>
                <CheckCircle2 size={14} strokeWidth={2} /> Mark Complete
              </>
            ) : (
              <>Mark Complete</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EntryMode() {
  const [data, setData] = useState(null);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [openJobId, setOpenJobId] = useState(null);
  const [sortMode, setSortMode] = useState("recent"); // "recent" | "alpha"
  // Weekly completion-pressure filter — when true, only jobs with at least one
  // unanswered MANUAL item are shown (both in Live Now and grouped-by-tech).
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [onlyInProgress, setOnlyInProgress] = useState(false);
  const [week, setWeek] = useUrlWeek(); // CC-205

  const load = async (opts = {}) => {
    // 2026-08-10 (Juliana feedback): after a checklist PATCH the modal used
    // to call `load()` which flipped loadState → "loading" and re-mounted
    // the entire Entry page, giving the "whole screen reloads on every
    // click" feel. Silent mode keeps the current UI mounted and only
    // swaps the underlying data on success; the modal itself is already
    // hydrated from the PATCH response.
    const silent = opts.silent === true;
    if (!silent) setLoadState({ status: "loading", error: null });
    try {
      const { data: resp } = await api.get(`/kpi/entry?week=${week}`);
      setData(resp);
      if (!silent) setLoadState({ status: "ok", error: null });
    } catch (err) {
      if (!silent) {
        setData(null);
        setLoadState({ status: "error", error: extractApiError(err) });
      }
      // Silent-mode failures are swallowed intentionally: the modal already
      // reflects the successful PATCH; a background reconciliation failure
      // shouldn't surface an error card on top of the user's live work.
    }
  };
  useEffect(() => { load(); }, [week]); // eslint-disable-line react-hooks/exhaustive-deps
  const switcher = <EntryWeekSwitcher currentWeekOf={data?.week_of} setWeek={setWeek} />;

  const isEmpty = loadState.status === "ok" && (!data || (data.total_jobs === 0 && (!data.techs || data.techs.length === 0)));
  if (loadState.status !== "ok" || isEmpty) {
    return (
      <div className="space-y-5">
      {switcher}
      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={isEmpty}
        onRetry={load}
        emptyTitle="No jobs this week yet"
        emptyBody={<>Once ServiceTitan syncs today’s jobs, they’ll appear here for KPI entry.</>}
        testIdPrefix="kpi-entry"
      />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="kpi-entry-mode">
      {switcher}
      {/* In-progress callout (2026-07-31) — visible only when at least one job
          has status="in_progress" (exact string sent by VPS/Agent 12 when a
          tech taps "Start Job" in the ServiceTitan mobile app). Click to
          filter the by-tech list to live jobs only. Muted red vs. the amber
          incomplete callout so the two visually distinguish. */}
      {(() => {
        const inProgressCount = (data.techs || []).reduce(
          (n, t) => n + (t.jobs || []).filter((j) => j.status === "in_progress").length, 0,
        );
        return inProgressCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyInProgress((v) => !v)}
            data-testid="kpi-in-progress-callout"
            aria-pressed={onlyInProgress}
            className={`w-full flex items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left transition-colors ${
              onlyInProgress
                ? "bg-[#fdecec] border-[#f4c9c9] hover:bg-[#fbdada]"
                : "bg-[#fff5f5] border-[#f4c9c9] hover:bg-[#fdecec]"
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[#f4c9c9] flex items-center justify-center shrink-0">
                <span className="w-2 h-2 rounded-full bg-[#c73535] animate-pulse" />
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#7a1c1c]">
                  <span data-testid="kpi-in-progress-count">{inProgressCount}</span>
                  {" job"}{inProgressCount === 1 ? "" : "s"}{" currently "}
                  <span className="font-mono-data uppercase text-[11.5px] tracking-widest">in progress</span>
                  {" — tech is at the customer."}
                </div>
                <div className="text-[11.5px] text-[#7a1c1c] mt-0.5 opacity-80">
                  {onlyInProgress ? "Filtered to in-progress jobs only. Click to clear." : "Click to filter to live jobs."}
                </div>
              </div>
            </div>
            <span
              data-testid="kpi-in-progress-filter-btn"
              className={`text-[11.5px] uppercase tracking-widest font-bold rounded-md px-2.5 py-1 border shrink-0 ${
                onlyInProgress ? "bg-[#7a1c1c] text-white border-[#7a1c1c]" : "bg-white text-[#7a1c1c] border-[#f4c9c9]"
              }`}
            >
              {onlyInProgress ? "Filtered · Clear" : "Show live"}
            </span>
          </button>
        );
      })()}

      {/* Weekly completion-pressure callout — only shown when at least one
          job on the current week has an unanswered MANUAL item. Click to
          filter both LIVE NOW and the by-tech list to only those jobs.
          Amber tone deliberately non-alarming: this is a manager-level
          heads-up, not an error state. */}
      {data.jobs_incomplete > 0 && (
        <button
          type="button"
          onClick={() => setOnlyIncomplete((v) => !v)}
          data-testid="kpi-incomplete-callout"
          aria-pressed={onlyIncomplete}
          className={`w-full flex items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left transition-colors ${
            onlyIncomplete
              ? "bg-[#fff4d6] border-[#f3d97a] hover:bg-[#ffedb8]"
              : "bg-[#fef7e6] border-[#f3d97a] hover:bg-[#fff4d6]"
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-[#f3d97a]/70 flex items-center justify-center shrink-0">
              <AlertTriangle size={16} strokeWidth={2} className="text-[#8a5b00]" />
            </div>
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold text-[#5a3a00]">
                <span data-testid="kpi-incomplete-count">{data.jobs_incomplete}</span>
                {" of "}
                <span>{data.total_jobs}</span>
                {" job"}{data.total_jobs === 1 ? "" : "s"}{" this week "}
                {data.jobs_incomplete === 1 ? "has" : "have"}{" unanswered checklist items."}
              </div>
              <div className="text-[11.5px] text-[#8a5b00] mt-0.5">
                {onlyIncomplete
                  ? "Filtered to incomplete jobs only. Click to clear."
                  : "Click to review just those jobs."}
              </div>
            </div>
          </div>
          <span
            data-testid="kpi-incomplete-filter-btn"
            className={`text-[11.5px] uppercase tracking-widest font-bold rounded-md px-2.5 py-1 border shrink-0 ${
              onlyIncomplete
                ? "bg-[#8a5b00] text-white border-[#8a5b00]"
                : "bg-white text-[#8a5b00] border-[#f3d97a]"
            }`}
          >
            {onlyIncomplete ? "Filtered · Clear" : "Review"}
          </span>
        </button>
      )}

      {/* Header aggregates */}
      <div className="bg-white border border-[#e7e9ee] rounded-xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Week</div>
          <div className="font-display font-semibold text-[15px] text-[#0e1420] mt-1">{data.week_range}</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Total Jobs</div>
          <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1" data-testid="entry-total-jobs">{data.total_jobs}</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Total Revenue</div>
          <div className="font-mono-data font-bold text-[22px] text-[#0e1420] mt-1" data-testid="entry-total-revenue">${data.total_revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">KPI Complete</div>
          <div className={`font-mono-data font-bold text-[22px] mt-1 ${
            data.kpi_complete_pct >= 75 ? "text-[#0e8a68]" : data.kpi_complete_pct >= 50 ? "text-[#8a6a1c]" : "text-[#ef5757]"
          }`} data-testid="entry-kpi-complete-pct">{data.kpi_complete_pct}%</div>
        </div>
      </div>

      {/* LIVE NOW */}
      {(() => {
        const liveShown = onlyIncomplete
          ? data.live_now.filter((j) => j.has_unanswered_manual)
          : data.live_now;
        if (liveShown.length === 0) return null;
        return (
          <section data-testid="live-now-section">
            <div className="flex items-center gap-2 mb-2.5">
              <Radio size={14} strokeWidth={1.8} className="text-[#ef5757] animate-pulse" />
              <div className="text-[11px] uppercase tracking-[.15em] text-[#ef5757] font-bold">Live Now — {liveShown.length}</div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {liveShown.map((j) => (
                <button
                  key={j.id}
                  onClick={() => setOpenJobId(j.id)}
                  data-testid={`live-job-${j.id}`}
                  data-incomplete={j.has_unanswered_manual ? "true" : "false"}
                  className="text-left bg-white border-2 border-[#ef5757] rounded-xl p-4 hover:shadow-md transition-shadow relative"
                >
                  {j.has_unanswered_manual && (
                    <span
                      title="Unanswered required items"
                      data-testid={`live-job-incomplete-dot-${j.id}`}
                      className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-[#e0a300] ring-2 ring-white"
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono-data uppercase tracking-widest bg-[#ef5757] text-white rounded px-2 py-0.5">Active</span>
                    <span className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">{j.job_number}</span>
                  </div>
                  <div className="mt-2 font-display font-semibold text-[15px] text-[#0e1420]">{j.tech_name}</div>
                  <div className="text-[12.5px] text-[#0e1420]">{j.customer}</div>
                  <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1">{fmtDayTime(j.scheduled_at)}</div>
                </button>
              ))}
            </div>
          </section>
        );
      })()}

      {/* ON HOLD (2026-08-01) — jobs that VPS re-POSTed with status="on_hold"
          because the ST job is mid-multi-visit and currently dormant (no tech
          on-site today). Same metric-quarantine as in_progress (server-side
          filters exclude anything != "completed"), but shown as a distinct
          section so CSRs can still PATCH checklist items during the gap.
          Muted slate palette vs. LIVE NOW's active-red — visual language
          reads as "real work, just paused". */}
      {(() => {
        const holdShown = onlyIncomplete
          ? (data.on_hold || []).filter((j) => j.has_unanswered_manual)
          : (data.on_hold || []);
        if (holdShown.length === 0) return null;
        return (
          <section data-testid="on-hold-section">
            <div className="flex items-center gap-2 mb-2.5">
              <PauseCircle size={14} strokeWidth={1.8} className="text-[#5c6877]" />
              <div className="text-[11px] uppercase tracking-[.15em] text-[#5c6877] font-bold">On Hold — {holdShown.length}</div>
              <span className="text-[10.5px] text-[color:var(--cc-ink-muted)] font-normal normal-case tracking-normal">Multi-visit jobs between appointments · not counted in metrics</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {holdShown.map((j) => (
                <button
                  key={j.id}
                  onClick={() => setOpenJobId(j.id)}
                  data-testid={`on-hold-job-${j.id}`}
                  data-incomplete={j.has_unanswered_manual ? "true" : "false"}
                  className="text-left bg-white border border-[#c9cfd8] rounded-xl p-4 hover:shadow-md transition-shadow relative"
                >
                  {j.has_unanswered_manual && (
                    <span
                      title="Unanswered required items"
                      data-testid={`on-hold-job-incomplete-dot-${j.id}`}
                      className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-[#e0a300] ring-2 ring-white"
                    />
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono-data uppercase tracking-widest bg-[#5c6877] text-white rounded px-2 py-0.5">On Hold</span>
                    <span className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">{j.job_number}</span>
                  </div>
                  <div className="mt-2 font-display font-semibold text-[15px] text-[#0e1420]">{j.tech_name}</div>
                  <div className="text-[12.5px] text-[#0e1420]">{j.customer}</div>
                  <div className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] mt-1">{fmtDayTime(j.scheduled_at)}</div>
                </button>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Grouped by tech */}
      <section>
        <div className="flex items-center justify-between gap-3 mb-2.5 flex-wrap">
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">Jobs by tech</div>
          <div className="inline-flex bg-white rounded-lg p-1 border border-[#e7e9ee]" data-testid="entry-sort-toggle">
            <span className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold self-center px-2">Completed:</span>
            {[
              { id: "recent", label: "Recent" },
              { id: "alpha", label: "Alpha" },
            ].map((o) => (
              <button
                key={o.id}
                onClick={() => setSortMode(o.id)}
                data-testid={`entry-sort-${o.id}`}
                className={`px-3 py-1 text-[11.5px] rounded-md font-semibold transition-colors ${
                  sortMode === o.id ? "bg-[#0072bc] text-white" : "text-[#0e1420] hover:bg-[#f5f6f8]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3">
          {[...data.techs]
            .map((t) => {
              // Apply both filters — incomplete-only (existing) and in-progress-only
              // (new 2026-07-31, keyed off the exact string "in_progress" that VPS
              // will send when a tech taps "Start Job" in the ServiceTitan mobile app).
              let jobs = t.jobs;
              if (onlyIncomplete) jobs = jobs.filter((j) => j.has_unanswered_manual);
              if (onlyInProgress) jobs = jobs.filter((j) => j.status === "in_progress");
              return { ...t, jobs };
            })
            .filter((t) => t.jobs.length > 0)
            .sort((a, b) => {
              if (sortMode === "alpha") return a.tech_name.localeCompare(b.tech_name);
              // "recent" — techs whose most-recent completed job is most recent, first.
              const lastCompletedTs = (t) => {
                const done = t.jobs.filter((j) => j.status === "completed").map(jobDisplayAt);
                return done.length ? Math.max(...done.map((s) => new Date(s).getTime())) : 0;
              };
              return lastCompletedTs(b) - lastCompletedTs(a);
            }).map((t) => (
            <div key={t.tech_name} className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid={`entry-tech-${t.tech_name}`}>
              <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-[#eef1f6] bg-[#fafbfc]">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[15px] text-[#0e1420] truncate">{t.tech_name}</div>
                  <div className="text-[11px] text-[color:var(--cc-ink-muted)]">{t.unit} · {t.jobs.length} job{t.jobs.length === 1 ? "" : "s"}{onlyIncomplete ? " needing attention" : onlyInProgress ? " live" : ""}</div>
                </div>
                {/* Per-tech total (2026-07-31): Entry mode intentionally includes
                    in_progress + on_hold + scheduled dollars — CSRs need pipeline
                    visibility here. Scorecard/Wall show completed-only. Label the
                    "open" slice inline so the two views don't look like they disagree.
                    2026-08-01: on_hold added — multi-visit gap jobs are still real
                    projected revenue and belong in the open-pipeline number. */}
                {(() => {
                  const openRevenue = (t.jobs || [])
                    .filter((j) => j.status === "in_progress" || j.status === "on_hold" || j.status === "scheduled")
                    .reduce((s, j) => s + (Number(j.revenue) || 0), 0);
                  const totalStr = `$${t.total_revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                  return (
                    <div className="text-right whitespace-nowrap">
                      <div className="font-mono-data font-bold text-[16px] text-[#0e1420]" data-testid={`entry-tech-total-${t.tech_name}`}>{totalStr}</div>
                      {openRevenue > 0 && (
                        <div
                          className="text-[10.5px] text-[color:var(--cc-ink-muted)] font-mono-data mt-0.5"
                          data-testid={`entry-tech-open-label-${t.tech_name}`}
                          title="Entry mode totals include in-progress and scheduled jobs (pipeline). Scorecard and Wall show completed-only booked revenue."
                        >
                          incl. ${openRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })} in open jobs
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <ul className="divide-y divide-[#eef1f6]">
                {t.jobs.map((j) => (
                  <li key={j.id}>
                    <button
                      onClick={() => setOpenJobId(j.id)}
                      data-testid={`entry-job-${j.id}`}
                      data-incomplete={j.has_unanswered_manual ? "true" : "false"}
                      className="w-full text-left px-5 py-2.5 grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 hover:bg-[#f5f6f8]"
                    >
                      <span className={`font-mono-data text-[11px] uppercase tracking-widest rounded px-2 py-0.5 border ${
                        j.status === "in_progress" ? "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]" :
                        j.status === "on_hold" ? "bg-[#eef0f3] text-[#5c6877] border-[#c9cfd8]" :
                        j.status === "scheduled" ? "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" :
                        "bg-[#f5f6f8] text-[#5c6877] border-[#e7e9ee]"
                      }`}>{j.job_number}</span>
                      <span className="min-w-0">
                        <span className="text-[13px] text-[#0e1420]">{j.customer}</span>
                        {j.status === "in_progress" && (
                          <span
                            data-testid={`entry-job-status-in-progress-${j.id}`}
                            title="Tech tapped &quot;Start Job&quot; — live at customer"
                            className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#c73535] bg-[#fdecec] border border-[#f4c9c9] rounded px-1.5 py-0.5"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#c73535] animate-pulse" />
                            In progress
                          </span>
                        )}
                        {j.status === "on_hold" && (
                          <span
                            data-testid={`entry-job-status-on-hold-${j.id}`}
                            title="Job started but currently dormant — multi-visit gap between appointments"
                            className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#5c6877] bg-[#eef0f3] border border-[#c9cfd8] rounded px-1.5 py-0.5"
                          >
                            <PauseCircle size={9} strokeWidth={2.2} />
                            On hold
                          </span>
                        )}
                        <span className="text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] block sm:inline sm:ml-2" data-testid={`entry-job-date-${j.id}`}>{fmtDayTime(jobDisplayAt(j))}</span>
                      </span>
                      {j.has_unanswered_manual ? (
                        <span
                          title="Unanswered required items"
                          data-testid={`entry-job-incomplete-dot-${j.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-mono-data uppercase tracking-widest text-[#8a5b00] bg-[#fff4d6] border border-[#f3d97a] rounded px-1.5 py-0.5"
                        >
                          <AlertTriangle size={10} strokeWidth={2} /> Incomplete
                        </span>
                      ) : <span />}
                      <span className="font-mono-data font-semibold text-[13px] text-[#0e1420] whitespace-nowrap">${j.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {onlyIncomplete && [...data.techs].every((t) => t.jobs.filter((j) => j.has_unanswered_manual).length === 0) && (
            <div
              data-testid="kpi-incomplete-empty"
              className="bg-white border border-[#e7e9ee] rounded-xl p-12 text-center text-[13.5px] text-[color:var(--cc-ink-muted)]"
            >
              All jobs are fully answered. Nothing to review.
            </div>
          )}
        </div>
      </section>

      {openJobId && (
        <JobDetailModal
          jobId={openJobId}
          onClose={() => setOpenJobId(null)}
          onUpdated={() => load({ silent: true })}
        />
      )}
    </div>
  );
}
